import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, IconButton, Paper, Stack, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs,
    TextField, Tooltip, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import { PERMISSIONS } from '../../../constants/permissions';
import { AuthService } from '../../../services/auth.service';
import { ManagedDashboardService } from '../../../services/managedDashboard.service';
import {
    DashboardDefault, DefaultAuditRow, MANAGED_BOARD_KEYS, MAX_PRIORITY, TargetDepartment, TargetRole, TEAM_PRIORITY_FLOOR,
} from '../../../interfaces/managedDashboard.interface';
import SearchableSelect from '../../common/SearchableSelect';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { useDashboardAccess } from '../useDashboardAccess';
import WidgetListEditor, { WidgetListValue } from './WidgetListEditor';

interface Option { id: string; label: string }

/** Flat list from the targets endpoint, indented by depth so a unit sits under its parent. */
const asOptions = (list: TargetDepartment[]): Option[] => {
    const byParent = new Map<string | null, TargetDepartment[]>();
    const ids = new Set(list.map(d => d.id));
    list.forEach(d => {
        const key = d.parentId && ids.has(d.parentId) ? d.parentId : null; // a head's subtree root has its parent outside the list
        byParent.set(key, [...(byParent.get(key) ?? []), d]);
    });
    const out: Option[] = [];
    const walk = (parent: string | null, depth: number) =>
        (byParent.get(parent) ?? []).forEach(d => { out.push({ id: d.id, label: `${'  '.repeat(depth)}${d.name}` }); walk(d.id, depth + 1); });
    walk(null, 0);
    return out;
};

const EMPTY: WidgetListValue = { widgetIds: [], lockedWidgetIds: [] };
const COMPANY = '';

/**
 * Settings, Dashboards, Role defaults. Company defaults (no department) need DASHBOARD_LAYOUT_MANAGE_ALL;
 * a department default needs MANAGE_ALL, or MANAGE_TEAM for a department the caller heads. The editor
 * lists widget names only; it never renders a widget with data.
 */
export default function RoleDefaultsPage() {
    const { hasPermission } = useAuth();
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution() ?? '';
    const canAll = hasPermission(PERMISSIONS.DASHBOARD_LAYOUT_MANAGE_ALL);
    const { enabledModules } = useDashboardAccess();

    const [tab, setTab] = useState<'defaults' | 'audit'>('defaults');
    const [roles, setRoles] = useState<TargetRole[]>([]);
    const [departments, setDepartments] = useState<Option[]>([]);
    const [boardKey, setBoardKey] = useState('home');
    const [roleId, setRoleId] = useState('');
    const [departmentId, setDepartmentId] = useState(COMPANY);
    const [priority, setPriority] = useState<string>(String(TEAM_PRIORITY_FLOOR));
    const [value, setValue] = useState<WidgetListValue>(EMPTY);
    const [defaults, setDefaults] = useState<DashboardDefault[]>([]);
    const [audit, setAudit] = useState<DefaultAuditRow[]>([]);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState<DashboardDefault | null>(null);
    const [baseline, setBaseline] = useState('');
    const [pendingChange, setPendingChange] = useState<(() => void) | null>(null);

    useEffect(() => {
        if (!institutionId) return;
        let live = true;
        // One call answers both pickers, already narrowed to what this caller may set (the backend decides).
        ManagedDashboardService.targets(institutionId)
            .then(t => { if (live) { setRoles(t.roles ?? []); setDepartments(asOptions(t.departments ?? [])); } })
            .catch(e => { if (live) toast.showApiError?.(e, 'Could not load the roles and departments'); });
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const loadDefaults = useCallback(() => {
        if (!institutionId) return;
        ManagedDashboardService.listDefaults(institutionId, boardKey)
            .then(setDefaults)
            .catch(e => { setDefaults([]); toast.showApiError?.(e, 'Could not load the defaults'); });
    }, [institutionId, boardKey, toast]);
    useEffect(() => { loadDefaults(); }, [loadDefaults]);

    useEffect(() => {
        if (tab !== 'audit' || !canAll || !institutionId) return;
        ManagedDashboardService.defaultsAudit(institutionId).then(setAudit)
            .catch(e => toast.showApiError?.(e, 'Could not load the change log'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, canAll, institutionId]);

    // Picking a role (+ department) loads the existing default for that slot, else starts empty.
    useEffect(() => {
        const found = defaults.find(d => d.roleId === roleId && (d.departmentId ?? COMPANY) === departmentId);
        const v = found ? { widgetIds: found.widgetIds, lockedWidgetIds: found.lockedWidgetIds } : EMPTY;
        const p = found ? String(found.priority) : String(canAll ? 100 : TEAM_PRIORITY_FLOOR);
        setValue(v);
        setPriority(p);
        setBaseline(JSON.stringify({ v, p }));
    }, [defaults, roleId, departmentId, canAll]);

    // Unsaved edits are lost when the board, role or department changes, so ask first.
    const dirty = !!roleId && JSON.stringify({ v: value, p: priority }) !== baseline;
    const guarded = (change: () => void) => { if (dirty) setPendingChange(() => change); else change(); };

    const roleName = useCallback((id: string) => roles.find(r => r.id === id)?.name ?? id, [roles]);
    const deptName = useCallback(
        (id: string | null) => (id ? (departments.find(d => d.id === id)?.label.trim() ?? id) : 'Whole company'),
        [departments],
    );
    const roleOptions = useMemo<Option[]>(() => roles.map(r => ({ id: r.id, label: r.name })), [roles]);
    const boardOptions = useMemo<Option[]>(() => MANAGED_BOARD_KEYS.map(b => ({ id: b.key, label: b.label })), []);

    const needsDepartment = !canAll; // a company default is MANAGE_ALL only
    const priorityNum = Number(priority);
    const priorityOk = priority !== '' && Number.isFinite(priorityNum) && priorityNum >= (canAll ? 0 : TEAM_PRIORITY_FLOOR)
        && priorityNum <= MAX_PRIORITY;
    const hasWidgets = value.widgetIds.length > 0; // the backend refuses an empty default: delete it instead
    const canSave = !!roleId && (!needsDepartment || !!departmentId) && priorityOk && hasWidgets && !saving;

    const save = async () => {
        setSaving(true);
        try {
            await ManagedDashboardService.saveDefault(institutionId, {
                roleId,
                departmentId: departmentId || null,
                boardKey,
                widgetIds: value.widgetIds,
                lockedWidgetIds: value.lockedWidgetIds,
                priority: priorityNum,
            });
            toast.showSuccess?.('Default saved');
            loadDefaults();
        } catch (e) {
            toast.showApiError?.(e, 'Could not save the default');
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleting) return;
        try {
            await ManagedDashboardService.deleteDefault(deleting.id);
            toast.showSuccess?.('Default deleted');
            setDeleting(null);
            loadDefaults();
        } catch (e) {
            toast.showApiError?.(e, 'Could not delete the default');
        }
    };

    return (
        <Box sx={{ p: 2, minWidth: 0 }} data-help-page="dashboard.roleDefaults">
            <Typography variant="h5" fontWeight={700} gutterBottom>Dashboard role defaults</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} data-help-section="dashboard.roleDefaults.intro">
                Choose the widgets a role starts with. People who never customised their board follow the default at once;
                locked widgets stay on everyone&apos;s board. A department default overrides the company default for that department and the units below it.
                You see widget names only, never anyone&apos;s data.
            </Typography>

            {canAll && (
                <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
                    <Tab data-help="dashboard.roleDefaults.tabDefaults" value="defaults" label="Defaults" />
                    <Tab data-help="dashboard.roleDefaults.tabAudit" value="audit" label="Change log" />
                </Tabs>
            )}

            {tab === 'audit' && canAll ? (
                <Paper variant="outlined" sx={{ overflowX: 'auto' }} data-testid="defaults-audit" data-help-section="dashboard.roleDefaults.auditLog">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="dashboard.roleDefaults.audit.when">When</TableCell><TableCell data-help="dashboard.roleDefaults.audit.who">Who</TableCell><TableCell data-help="dashboard.roleDefaults.audit.action">Action</TableCell>
                                <TableCell data-help="dashboard.roleDefaults.audit.board">Board</TableCell><TableCell data-help="dashboard.roleDefaults.audit.role">Role</TableCell><TableCell data-help="dashboard.roleDefaults.audit.department">Department</TableCell>
                                <TableCell data-help="dashboard.roleDefaults.audit.before">Before</TableCell><TableCell data-help="dashboard.roleDefaults.audit.after">After</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {audit.length === 0 && <TableRow><TableCell colSpan={8}>No changes recorded.</TableCell></TableRow>}
                            {audit.map(a => (
                                <TableRow key={a.id}>
                                    <TableCell>{new Date(a.at).toLocaleString()}</TableCell>
                                    <TableCell>{a.actorKind}</TableCell>
                                    <TableCell>{a.action}</TableCell>
                                    <TableCell>{a.boardKey}</TableCell>
                                    <TableCell>{a.roleId ? roleName(a.roleId) : ''}</TableCell>
                                    <TableCell>{deptName(a.departmentId)}</TableCell>
                                    <TableCell>{(a.before ?? []).length} widgets</TableCell>
                                    <TableCell>{(a.after ?? []).length} widgets</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            ) : (
                <>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }} data-help-section="dashboard.roleDefaults.filters">
                        <Box data-help="dashboard.roleDefaults.board" sx={{ flex: '1 1 240px', maxWidth: 360 }}>
                            <SearchableSelect<Option> label="Board" size="small" clearable={false} options={boardOptions} value={boardKey}
                                getOptionLabel={o => o.label} onChange={id => id && id !== boardKey && guarded(() => setBoardKey(id))} data-testid="board-select" />
                        </Box>
                        <Box data-help="dashboard.roleDefaults.role" sx={{ flex: '1 1 240px', maxWidth: 360 }}>
                            <SearchableSelect<Option> label="Role" size="small" options={roleOptions} value={roleId}
                                getOptionLabel={o => o.label} onChange={id => id !== roleId && guarded(() => setRoleId(id))} data-testid="role-select" />
                        </Box>
                        <Box data-help="dashboard.roleDefaults.department" sx={{ flex: '1 1 240px', maxWidth: 360 }}>
                            <SearchableSelect<Option> label={canAll ? 'Department (empty = whole company)' : 'Department'} size="small"
                                options={departments} value={departmentId} required={needsDepartment}
                                getOptionLabel={o => o.label.trim()} onChange={id => id !== departmentId && guarded(() => setDepartmentId(id))} data-testid="department-select" />
                        </Box>
                        <Box data-help="dashboard.roleDefaults.priority" sx={{ flex: '1 1 160px', maxWidth: 220 }}>
                            <TextField label="Priority" size="small" type="number" value={priority} onChange={e => setPriority(e.target.value)}
                                fullWidth error={!priorityOk}
                                helperText={canAll ? `Lower wins when a person has several roles (0 to ${MAX_PRIORITY})` : `Lower wins; department heads start at ${TEAM_PRIORITY_FLOOR}`}
                                inputProps={{ min: canAll ? 0 : TEAM_PRIORITY_FLOOR, max: MAX_PRIORITY, 'aria-label': 'Priority' }} />
                        </Box>
                    </Box>

                    {!canAll && (
                        <Alert severity="info" sx={{ mb: 2 }} data-help-section="dashboard.roleDefaults.headNote">
                            You can set defaults for the departments you head. Your priority cannot go below {TEAM_PRIORITY_FLOOR}, so if your default and an admin's default tie for the same person, the admin's default wins.
                        </Alert>
                    )}

                    {roleId ? (
                        <Paper variant="outlined" sx={{ p: 2, mb: 3 }} data-testid="default-editor" data-help-section="dashboard.roleDefaults.editor">
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                                {roleName(roleId)} · {deptName(departmentId || null)}
                            </Typography>
                            <WidgetListEditor value={value} onChange={setValue} enabledModules={enabledModules} />
                            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                                <Button data-help="dashboard.roleDefaults.save" variant="contained" disabled={!canSave} onClick={save}>Save default</Button>
                            </Stack>
                            {!hasWidgets && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }} data-testid="empty-default-hint" data-help-section="dashboard.roleDefaults.emptyHint">
                                    A default needs at least one widget. To remove this default, use Delete in the table below.
                                </Typography>
                            )}
                        </Paper>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }} data-help-section="dashboard.roleDefaults.pickRole">Pick a role to edit its default.</Typography>
                    )}

                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Existing defaults for this board</Typography>
                    <Paper variant="outlined" sx={{ overflowX: 'auto' }} data-help-section="dashboard.roleDefaults.existing">
                        <Table size="small" data-testid="defaults-table">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="dashboard.roleDefaults.col.role">Role</TableCell><TableCell data-help="dashboard.roleDefaults.col.department">Department</TableCell><TableCell data-help="dashboard.roleDefaults.col.widgets">Widgets</TableCell>
                                    <TableCell data-help="dashboard.roleDefaults.col.locked">Locked</TableCell><TableCell data-help="dashboard.roleDefaults.col.priority">Priority</TableCell><TableCell data-help="dashboard.roleDefaults.col.actions" align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {defaults.length === 0 && <TableRow><TableCell colSpan={6}>No defaults yet for this board.</TableCell></TableRow>}
                                {defaults.map(d => (
                                    <TableRow key={d.id} data-testid={`default-row-${d.id}`}>
                                        <TableCell>{roleName(d.roleId)}</TableCell>
                                        <TableCell>{deptName(d.departmentId)}</TableCell>
                                        <TableCell>{d.widgetIds.length}</TableCell>
                                        <TableCell>{d.lockedWidgetIds.length}</TableCell>
                                        <TableCell>{d.priority}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Edit"><IconButton data-help="dashboard.roleDefaults.edit" size="small" aria-label={`Edit default for ${roleName(d.roleId)}`}
                                                onClick={() => guarded(() => { setRoleId(d.roleId); setDepartmentId(d.departmentId ?? COMPANY); })}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                            <Tooltip title="Delete"><IconButton data-help="dashboard.roleDefaults.delete" size="small" color="error" aria-label={`Delete default for ${roleName(d.roleId)}`}
                                                onClick={() => setDeleting(d)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                </>
            )}

            <ConfirmDialog
                open={!!pendingChange}
                title="Discard unsaved changes?"
                message="You changed this default but did not save it. Switching now loses those edits."
                confirmLabel="Discard"
                confirmColor="error"
                onCancel={() => setPendingChange(null)}
                onConfirm={() => { const go = pendingChange; setPendingChange(null); go?.(); }}
            />
            <ConfirmDialog
                open={!!deleting}
                title="Delete this default?"
                message="People who never customised their board fall back to the next level. Locks from this default are lifted."
                confirmLabel="Delete"
                confirmColor="error"
                onCancel={() => setDeleting(null)}
                onConfirm={confirmDelete}
            />
        </Box>
    );
}
